import React, { useState, useEffect } from 'react'
import {
  Plug,
  X,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Rocket,
  MessageCircle,
  Globe,
  Zap,
  KeyRound,
  Info,
  Loader2,
} from 'lucide-react'
import { createChannelInstance, validateTelegramToken } from '@/services/channelsApi'
import WhatsAppEmbeddedSignupButton from '@/components/channels/WhatsAppEmbeddedSignupButton'

type ChannelType = 'WHATSAPP' | 'WHATSAPP_CLOUD' | 'TELEGRAM' | 'WEBCHAT'

/**
 * Feature flag para ocultar temporalmente la conexión no oficial vía WhatsApp Web (QR / Baileys)
 * durante la Revisión de la App (App Review) de Meta.
 * Para restaurarla tras la aprobación, basta con definir VITE_ENABLE_UNOFFICIAL_WHATSAPP_QR=true en .env
 */
export const ENABLE_UNOFFICIAL_WHATSAPP_QR =
  import.meta.env.VITE_ENABLE_UNOFFICIAL_WHATSAPP_QR === 'true'

interface Props {
  onClose: () => void
  onSuccess: (instanceId: string, channelType: string) => void
  initialStep?: 'select' | 'configure'
  initialChannelType?: ChannelType
  initialShowManualCloud?: boolean
  enableUnofficialQr?: boolean
}

interface ChannelOption {
  readonly type: ChannelType
  readonly label: string
  readonly Icon: React.ComponentType<{ className?: string }>
  readonly color: string
  readonly bg: string
  readonly activeBg: string
  readonly description: string
}

const ALL_CHANNELS: readonly ChannelOption[] = [
  {
    type: 'WHATSAPP',
    label: 'WhatsApp (QR)',
    Icon: MessageCircle,
    color: 'text-green-600',
    bg: 'bg-green-50/70 border-green-200',
    activeBg: 'bg-green-50 border-green-500 ring-2 ring-green-500/20',
    description: 'Conecta escaneando un código QR desde tu teléfono',
  },
  {
    type: 'WHATSAPP_CLOUD',
    label: 'WhatsApp Cloud API',
    Icon: Zap,
    color: 'text-emerald-700',
    bg: 'bg-emerald-50/70 border-emerald-200',
    activeBg: 'bg-emerald-50 border-emerald-500 ring-2 ring-emerald-500/20',
    description: 'Conexión oficial de Meta API (para empresas)',
  },
  {
    type: 'WEBCHAT',
    label: 'WebChat',
    Icon: Globe,
    color: 'text-purple-600',
    bg: 'bg-purple-50/70 border-purple-200',
    activeBg: 'bg-purple-50 border-purple-500 ring-2 ring-purple-500/20',
    description: 'Widget de chat embebible en tu sitio web',
  },
]

export default function CreateChannelInstanceModal({
  onClose,
  onSuccess,
  initialStep = 'select',
  initialChannelType,
  initialShowManualCloud = false,
  enableUnofficialQr = ENABLE_UNOFFICIAL_WHATSAPP_QR,
}: Props) {
  const CHANNELS = enableUnofficialQr
    ? ALL_CHANNELS
    : ALL_CHANNELS.filter(c => c.type !== 'WHATSAPP')
  const defaultType: ChannelType =
    initialChannelType ?? (enableUnofficialQr ? 'WHATSAPP' : 'WHATSAPP_CLOUD')
  const [step, setStep] = useState<'select' | 'configure'>(initialStep)
  const [selectedType, setSelectedType] = useState<ChannelType>(defaultType)
  const [instanceName, setInstanceName] = useState('')
  const [botToken, setBotToken] = useState('')
  const [telegramReceiveMode, setTelegramReceiveMode] = useState<'webhook' | 'polling'>('webhook')
  const [validatingToken, setValidatingToken] = useState(false)
  const [telegramBotInfo, setTelegramBotInfo] = useState<{
    id: number
    username: string | null
    firstName: string | null
  } | null>(null)
  const [cloudPhoneNumberId, setCloudPhoneNumberId] = useState('')
  const [cloudAccessToken, setCloudAccessToken] = useState('')
  const [showManualCloud, setShowManualCloud] = useState(initialShowManualCloud)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedChannel = CHANNELS.find(c => c.type === selectedType) ?? CHANNELS[0]
  const SelectedIcon = selectedChannel.Icon

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [loading, onClose])

  const handleValidateTelegramToken = async () => {
    const token = botToken.trim()
    if (!token) {
      setError('Ingresa el token del bot para validarlo.')
      return
    }

    try {
      setValidatingToken(true)
      setError(null)
      const result = await validateTelegramToken(token)
      if (!result.valid || !result.bot) {
        setTelegramBotInfo(null)
        setError(result.error || 'No se pudo validar el token')
        return
      }

      setTelegramBotInfo(result.bot)
    } catch (err: unknown) {
      setTelegramBotInfo(null)
      const errObj = err as { response?: { data?: { error?: string; message?: string } } }
      setError(
        errObj?.response?.data?.error ||
          errObj?.response?.data?.message ||
          'Token de Telegram inválido'
      )
    } finally {
      setValidatingToken(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = instanceName.trim()
    const effectiveName = trimmed || (selectedType === 'WHATSAPP_CLOUD' ? 'WhatsApp Cloud' : '')
    if (!effectiveName) {
      setError('El nombre de la sesión es requerido.')
      return
    }

    const config: Record<string, string> = {}
    if (selectedType === 'TELEGRAM') {
      if (!botToken.trim()) {
        setError('El token del bot es requerido para Telegram.')
        return
      }
      config.botToken = botToken.trim()
      config.receiveMode = telegramReceiveMode
    } else if (selectedType === 'WHATSAPP_CLOUD') {
      if (!cloudPhoneNumberId.trim() || !cloudAccessToken.trim()) {
        setError('El Phone Number ID y el Access Token son requeridos.')
        return
      }
      config.phoneNumberId = cloudPhoneNumberId.trim()
      config.accessToken = cloudAccessToken.trim()
    }

    try {
      setLoading(true)
      setError(null)
      const instance = await createChannelInstance({
        channelType: selectedType,
        instanceName: trimmed,
        config,
      })
      onSuccess(instance.id, instance.channelType)
    } catch (err: unknown) {
      const errObj = err as { response?: { data?: { error?: string } } }
      setError(errObj?.response?.data?.error || 'Error al crear la instancia')
    } finally {
      setLoading(false)
    }
  }

  const showSubmitAction = !(selectedType === 'WHATSAPP_CLOUD' && !showManualCloud)

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
      onClick={e => {
        if (e.target === e.currentTarget && !loading) {
          onClose()
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-channel-modal-title"
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[92dvh] sm:max-h-[88vh] flex flex-col overflow-hidden transition-all"
      >
        {/* Cabecera fija */}
        <div className="shrink-0 px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
              <Plug className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 id="create-channel-modal-title" className="text-base sm:text-lg font-bold text-gray-900 truncate">
                {step === 'select' ? 'Nuevo Canal' : `Configurar ${selectedChannel.label}`}
              </h2>
              <p className="text-xs text-gray-500 truncate">
                {step === 'select'
                  ? 'Conecta un nuevo canal de mensajería'
                  : selectedChannel.description}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {step === 'configure' && (
              <button
                type="button"
                onClick={() => {
                  setError(null)
                  setStep('select')
                }}
                disabled={loading}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Cambiar canal</span>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              aria-label="Cerrar modal"
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Paso 1: Selección de canal */}
        {step === 'select' && (
          <>
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-2.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                Elige el tipo de canal
              </p>
              {CHANNELS.map(ch => {
                const ChannelIcon = ch.Icon
                const isSelected = selectedType === ch.type
                return (
                  <button
                    key={ch.type}
                    type="button"
                    onClick={() => setSelectedType(ch.type)}
                    className={`w-full flex items-center gap-3.5 p-3 rounded-xl border transition-all text-left ${
                      isSelected ? ch.activeBg : `${ch.bg} hover:opacity-90`
                    }`}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white shadow-xs shrink-0">
                      <ChannelIcon className={`w-5 h-5 ${ch.color}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-900 text-sm">{ch.label}</p>
                      <p className="text-xs text-gray-500">{ch.description}</p>
                    </div>
                    {isSelected && (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    )}
                  </button>
                )
              })}
            </div>

            <div className="shrink-0 px-5 py-3.5 border-t border-gray-100 bg-gray-50/80 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  setError(null)
                  setStep('configure')
                }}
                className="inline-flex items-center justify-center gap-1.5 px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm transition-colors"
              >
                <span>Continuar</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </>
        )}

        {/* Paso 2: Configuración del canal */}
        {step === 'configure' && (
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-4">
              {/* Tarjeta compacta del canal seleccionado (para canales distintos a Cloud API o como referencia visual limpia) */}
              {selectedType !== 'WHATSAPP_CLOUD' && (
                <div className={`flex items-center gap-3 p-3 rounded-xl border ${selectedChannel.bg}`}>
                  <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow-xs shrink-0">
                    <SelectedIcon className={`w-5 h-5 ${selectedChannel.color}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800 text-sm">{selectedChannel.label}</p>
                    <p className="text-xs text-gray-500">{selectedChannel.description}</p>
                  </div>
                </div>
              )}

              {/* Selector segmentado exclusivo para WhatsApp Cloud API */}
              {selectedType === 'WHATSAPP_CLOUD' && (
                <div className="space-y-3.5">
                  <div
                    role="tablist"
                    aria-label="Método de conexión de WhatsApp Cloud API"
                    className="grid grid-cols-2 gap-1 p-1 bg-gray-100 rounded-xl"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={!showManualCloud}
                      onClick={() => {
                        setError(null)
                        setShowManualCloud(false)
                      }}
                      className={`flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                        !showManualCloud
                          ? 'bg-white text-emerald-700 shadow-xs'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Zap className="w-3.5 h-3.5 shrink-0" />
                      <span>Conexión Rápida (Meta)</span>
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={showManualCloud}
                      onClick={() => {
                        setError(null)
                        setShowManualCloud(true)
                      }}
                      className={`flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                        showManualCloud
                          ? 'bg-white text-gray-900 shadow-xs'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <KeyRound className="w-3.5 h-3.5 shrink-0" />
                      <span>Credenciales Manuales</span>
                    </button>
                  </div>

                  {!showManualCloud && (
                    <WhatsAppEmbeddedSignupButton
                      compact
                      onSuccess={result => {
                        const instId = result?.channelInstanceId || 'cloud'
                        onSuccess(instId, 'WHATSAPP_CLOUD')
                        onClose()
                      }}
                    />
                  )}
                </div>
              )}

              {/* Campo Nombre de la instancia (visible en todos excepto en conexión rápida de Meta) */}
              {showSubmitAction && (
                <div>
                  <label
                    htmlFor="channel-instance-name"
                    className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                  >
                    Nombre de la instancia <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="channel-instance-name"
                    type="text"
                    value={instanceName}
                    onChange={e => setInstanceName(e.target.value)}
                    placeholder="Ej: Soporte WhatsApp, Bot Ventas..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={loading}
                    maxLength={100}
                  />
                </div>
              )}

              {/* Campos específicos de WhatsApp Cloud Manual */}
              {selectedType === 'WHATSAPP_CLOUD' && showManualCloud && (
                <div className="space-y-3">
                  <div>
                    <label
                      htmlFor="cloud-phone-number-id"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      Phone Number ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="cloud-phone-number-id"
                      type="text"
                      value={cloudPhoneNumberId}
                      onChange={e => setCloudPhoneNumberId(e.target.value)}
                      placeholder="Ej: 105943288..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="cloud-access-token"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      Access Token <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="cloud-access-token"
                      type="password"
                      value={cloudAccessToken}
                      onChange={e => setCloudAccessToken(e.target.value)}
                      placeholder="EAA..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs sm:text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      disabled={loading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Genera tu Access Token permanente en el panel de desarrolladores de Meta.
                    </p>
                  </div>
                </div>
              )}

              {/* Campos específicos de Telegram */}
              {selectedType === 'TELEGRAM' && (
                <div className="space-y-3">
                  <div>
                    <label
                      htmlFor="telegram-bot-token"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      Token del Bot <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        id="telegram-bot-token"
                        type="text"
                        value={botToken}
                        onChange={e => {
                          setBotToken(e.target.value)
                          setTelegramBotInfo(null)
                        }}
                        placeholder="123456789:ABCDEF..."
                        className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                        disabled={loading || validatingToken}
                      />
                      <button
                        type="button"
                        onClick={handleValidateTelegramToken}
                        disabled={loading || validatingToken}
                        className="px-3 py-2 border border-blue-200 text-blue-700 bg-blue-50 rounded-lg text-sm font-medium hover:bg-blue-100 disabled:opacity-50 shrink-0"
                      >
                        {validatingToken ? 'Validando...' : 'Validar'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Obtén el token desde{' '}
                      <a
                        href="https://t.me/BotFather"
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 underline"
                      >
                        @BotFather
                      </a>{' '}
                      en Telegram
                    </p>
                  </div>

                  <div>
                    <label
                      htmlFor="telegram-receive-mode"
                      className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                    >
                      Modo de recepción
                    </label>
                    <select
                      id="telegram-receive-mode"
                      value={telegramReceiveMode}
                      onChange={e => setTelegramReceiveMode(e.target.value as 'webhook' | 'polling')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={loading}
                    >
                      <option value="webhook">Webhook</option>
                      <option value="polling">Long Polling</option>
                    </select>
                  </div>

                  {telegramBotInfo && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs sm:text-sm text-blue-900 space-y-0.5">
                      <p className="font-semibold">Bot validado correctamente</p>
                      <p>
                        ID: <span className="font-mono">{telegramBotInfo.id}</span>
                      </p>
                      <p>Nombre: {telegramBotInfo.firstName || '-'}</p>
                      <p>Username: {telegramBotInfo.username ? `@${telegramBotInfo.username}` : '-'}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Información de WebChat */}
              {selectedType === 'WEBCHAT' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-start gap-2 text-xs text-purple-800">
                  <Info className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
                  <span>
                    Después de crear la instancia obtendrás un snippet de código para embeber en tu web.
                  </span>
                </div>
              )}

              {/* Mensaje de error */}
              {error && (
                <div
                  role="alert"
                  className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs sm:text-sm text-red-700 flex items-start gap-2"
                >
                  <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            {/* Pie fijo de acciones */}
            <div className="shrink-0 px-5 py-3.5 border-t border-gray-100 bg-gray-50/80 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
              >
                Cancelar
              </button>
              {showSubmitAction && (
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-1.5 px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Creando...</span>
                    </>
                  ) : (
                    <>
                      <Rocket className="w-4 h-4" />
                      <span>Crear Canal</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

